import {
  Component,
  OnInit,
  OnDestroy,
  Inject,
  PLATFORM_ID,
} from "@angular/core";
import { DOCUMENT } from "@angular/common";
import Lenis from "lenis";
import {
  Router,
  NavigationEnd,
  RouterOutlet,
  ActivatedRoute,
} from "@angular/router";
import { HeaderComponent } from "./components/header/header.component";
import { FooterComponent } from "./components/footer/footer.component";
import { SupabaseService } from "./services/supabase.service";
import { FeedbackComponent } from "./components/feedback/feedback.component";
import { LoginComponent } from "./pages/login/login.component";
import { ToastContainerComponent } from "./components/toast-container/toast-container.component";
import { ProfileFeatureBannerComponent } from "./components/profile-feature-banner/profile-feature-banner.component";
import { AuthService } from "./services/auth.service";
import { LayoutService } from "./services/layout.service";
import { CommonModule } from "@angular/common";
import { User } from "@supabase/supabase-js";
import { filter, takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { isPlatformBrowser } from "@angular/common";
import { injectSpeedInsights } from "@vercel/speed-insights";
import { WebVitalsService } from "./services/web-vitals.service";

@Component({
  selector: "app-root",
  standalone: true,
  imports: [
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    FeedbackComponent,
    LoginComponent,
    ToastContainerComponent,
    ProfileFeatureBannerComponent,
    CommonModule,
  ],
  templateUrl: "./app.component.html",
})
export class AppComponent implements OnInit, OnDestroy {
  title = "DesignJobs";
  showLoginModal = false;
  showHeader = true;
  showFooter = true;
  showFeedback = true;
  isJobMatchesPage = false;
  private isBrowser: boolean;
  private lenis: Lenis | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private supabaseService: SupabaseService,
    public authService: AuthService,
    public layoutService: LayoutService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private webVitalsService: WebVitalsService,
    @Inject(PLATFORM_ID) private platformId: object,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    // Initialize Supabase at app startup
    this.supabaseService.getClient();

    // Subscribe to router events to update layout. Use setTimeout so the
    // active route component (e.g. PublicProfileComponent) can set layout
    // flags in ngOnInit before we read them.
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => setTimeout(() => this.updateLayoutFromRoute(), 0));
  }

  private updateLayoutFromRoute(): void {
    const url = this.router.url.split("?")[0];
    this.showHeader =
      url !== "/ai-job-matcher" && !this.layoutService.isPublicProfilePage;
    const hideFooterPaths = [
      "/ai-job-matcher",
      "/job-matches",
      "/dashboard",
      "/job-alerts",
      "/profile",
    ];
    this.showFooter =
      !this.authService.isAuthenticated() &&
      !hideFooterPaths.includes(url) &&
      !url.startsWith("/talent/") &&
      !this.layoutService.isPublicProfilePage;
    this.showFeedback = url !== "/job-matches";
    this.isJobMatchesPage = url === "/job-matches";
    if (this.isBrowser) {
      if (this.isJobMatchesPage) {
        this.document.body.classList.add("job-matches-page");
      } else {
        this.document.body.classList.remove("job-matches-page");
      }
      const isWhiteBodyPage =
        url === "/" ||
        url === "" ||
        url === "/profile" ||
        url.startsWith("/talent/") ||
        this.layoutService.isPublicProfilePage;
      const shouldBeWhite =
        this.authService.isAuthenticated() || isWhiteBodyPage;
      if (shouldBeWhite) {
        this.document.body.classList.add("white");
      } else {
        this.document.body.classList.remove("white");
      }
    }
  }

  ngOnInit(): void {
    this.updateLayoutFromRoute();

    // Subscribe to login modal state
    this.authService.showLoginModal$.subscribe(
      (show) => (this.showLoginModal = show)
    );

    // Toggle body class and layout (footer, etc.) when auth state changes
    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        if (this.isBrowser) {
          if (user) {
            this.document.body.classList.add("logged-in", "white");
          } else {
            this.document.body.classList.remove("logged-in", "white");
          }
        }
        this.updateLayoutFromRoute();
      });

    // Add WebSite schema for site-wide Google site name recognition
    this.addWebsiteSchema();

    // Initialize Vercel Speed Insights (client-side only)
    if (this.isBrowser) {
      injectSpeedInsights();
      
      // Initialize Core Web Vitals monitoring
      this.webVitalsService.initWebVitals();
    }

    // Initialize Lenis smooth scroll (client-side only)
    if (this.isBrowser) {
      this.lenis = new Lenis({
        autoRaf: true,
        prevent: (node: Element) => {
          // Allow native scroll on elements with data-lenis-prevent attribute
          // or elements inside a container with that attribute
          return node.closest("[data-lenis-prevent]") !== null;
        },
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.lenis?.destroy();
    this.lenis = null;
  }

  // Handle login completion
  onLoginComplete(user: User): void {
    this.authService.closeLoginModal();

    const pendingAction = this.authService.getPendingActionAfterLogin(); // Clears from AuthService
    const redirectPath = this.authService.getRedirectPath(); // Clears from AuthService

    if (pendingAction === "ai_matcher_submitFinal") {
      this.router
        .navigate(["/ai-job-matcher"], {
          queryParams: { action: "submitFinal" },
        })
        .then(() => {
          // Scroll to top after navigation
          if (this.isBrowser) {
            window.scrollTo(0, 0);
          }
        });
    } else if (pendingAction === "ai_matcher_onboarding") {
      // IMPORTANT: Set the pending action AGAIN because it was cleared by getPendingActionAfterLogin()
      // The AI Job Matcher component needs to read it on initialization
      this.authService.setPendingActionAfterLogin('ai_matcher_onboarding');
      
      this.router.navigate(["/ai-job-matcher"]).then(() => {
        // Scroll to top after navigation
        if (this.isBrowser) {
          window.scrollTo(0, 0);
        }
      });
    } else if (pendingAction === "apply_to_job") {
      // Process the pending job application
      this.authService.processPendingApplication();
      // Don't navigate away - user should stay on current page
    } else if (pendingAction === "resume_analysis") {
      // Don't navigate away - user should stay on job details page to analyze resume
    } else if (redirectPath && redirectPath !== "/") {
      this.router.navigateByUrl(redirectPath).then(() => {
        // Scroll to top after navigation
        if (this.isBrowser) {
          window.scrollTo(0, 0);
        }
      });
    } else {
      this.router.navigateByUrl("/dashboard").then(() => {
        // Scroll to top after navigation
        if (this.isBrowser) {
          window.scrollTo(0, 0);
        }
      });
    }
  }

  // Method to close the login modal
  closeLoginModal(): void {
    this.authService.closeLoginModal();
  }

  /**
   * Injects WebSite and Organization JSON-LD into the document head.
   * Runs on both server (SSR) and browser so crawlers see schemas in initial HTML.
   * Uses DOCUMENT token so Angular Universal injects into the server-rendered document.
   */
  private addWebsiteSchema(): void {
    const head = this.document.head;
    if (!head) return;

    const existingWebsiteScript = this.document.getElementById("websiteSchema");
    if (existingWebsiteScript) existingWebsiteScript.remove();

    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Design Jobs",
      alternateName: ["Design Jobs", "DesignJobs"],
      url: "https://designjobs.careers",
      description:
        "Find the best design jobs at top companies. Browse hundreds of UI/UX, Product design, graphic design, and design leadership opportunities.",
      potentialAction: {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate:
            "https://designjobs.careers/jobs?search={search_term_string}",
        },
        "query-input": "required name=search_term_string",
      },
    };

    const websiteScript = this.document.createElement("script");
    websiteScript.id = "websiteSchema";
    websiteScript.type = "application/ld+json";
    websiteScript.text = JSON.stringify(websiteSchema);
    head.appendChild(websiteScript);

    const existingOrgScript = this.document.getElementById("organizationSchema");
    if (existingOrgScript) existingOrgScript.remove();

    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Design Jobs Careers",
      url: "https://designjobs.careers",
      logo: "https://designjobs.careers/assets/svgs/logo.png",
      sameAs: [
        "https://www.linkedin.com/company/designjobs-careers",
        "https://twitter.com/Designjobs6",
      ],
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer service",
        email: "info@designjobs.careers",
        url: "https://designjobs.careers/contact",
      },
      founder: {
        "@type": "Person",
        name: "Carlos Palma",
      },
    };

    const orgScript = this.document.createElement("script");
    orgScript.id = "organizationSchema";
    orgScript.type = "application/ld+json";
    orgScript.text = JSON.stringify(organizationSchema);
    head.appendChild(orgScript);
  }
}
